import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { StateProvider } from "@/context/StateContext";
import { AuthProvider } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { RequireEntitlement } from "@/components/RequireEntitlement";
import { RequireRole } from "@/components/RequireRole";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Customer journey (public)
import SalesPage from "@/pages/SalesPage";
import PurchaseSuccess from "@/pages/PurchaseSuccess";
import PurchaseCancel from "@/pages/PurchaseCancel";
import Checkout from "@/pages/Checkout";
import Activate from "@/pages/Activate";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import { DashboardHome, DashboardSprint, DashboardGoal, DashboardProgress, DashboardProfile } from "@/pages/DashboardPages";
import LifeHappened from "@/pages/LifeHappened";
import FoundationEdit from "@/pages/FoundationEdit";
import MoneyLanded from "@/pages/MoneyLanded";
import AssignUnassigned from "@/pages/AssignUnassigned";
import ExpectedIncome from "@/pages/ExpectedIncome";
import HelpCenter from "@/pages/HelpCenter";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminSupport from "@/pages/AdminSupport";
import AdminAccess from "@/pages/AdminAccess";
import ConfirmEmailChange from "@/pages/ConfirmEmailChange";
import Legal from "@/pages/Legal";

// Preserved product app (calculation engine)
import Home from "@/pages/Home";
import MySprint from "@/pages/MySprint";
import MyGoal from "@/pages/MyGoal";
import Progress from "@/pages/Progress";
import Profile from "@/pages/Profile";
import FoundationSetup from "@/pages/FoundationSetup";

const gated = (el) => <RequireEntitlement>{el}</RequireEntitlement>;
const dash = (el) => <RequireEntitlement requireOnboarded>{el}</RequireEntitlement>;
const app = (el) => gated(<AppLayout>{el}</AppLayout>);
const adminOnly = (el) => <RequireRole>{el}</RequireRole>;
const ownerOnly = (el) => <RequireRole owner>{el}</RequireRole>;

// Resets scroll on route change so a new page never inherits the previous
// page's scroll offset. A valid hash scrolls to its section once rendered
// (e.g. /legal#refund); the Google OAuth return hash is left untouched.
function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (hash && hash.includes("session_id=")) return; // OAuth callback hash
    if (hash && hash.length > 1) {
      const id = decodeURIComponent(hash.slice(1));
      let tries = 0;
      const tick = () => {
        const el = document.getElementById(id);
        if (el) { el.scrollIntoView(); return; }
        if (tries++ < 20) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      return;
    }
    window.scrollTo(0, 0);
    // `key` is included so re-navigating to the same URL (e.g. clicking a
    // generic /legal link while already on /legal and scrolled down) re-fires
    // this effect and returns the page to the top.
  }, [pathname, hash, key]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Google OAuth returns to {origin}/...#session_id=... - process it FIRST,
  // synchronously during render, before any protected route runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <ErrorBoundary routeKey={location.pathname}>
    <ScrollManager />
    <Routes>
      {/* Sales -> Stripe -> Success -> Activation */}
      <Route path="/" element={<SalesPage />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/purchase/success" element={<PurchaseSuccess />} />
      <Route path="/purchase/cancel" element={<PurchaseCancel />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
      <Route path="/legal" element={<Legal />} />

      {/* Gated per-user Foundation onboarding */}
      <Route path="/onboarding" element={gated(<Onboarding />)} />

      {/* Per-user dashboard shell (entitled + onboarding complete) */}
      <Route path="/dashboard" element={dash(<DashboardHome />)} />
      <Route path="/dashboard/money-landed" element={dash(<MoneyLanded />)} />
      <Route path="/dashboard/assign" element={dash(<AssignUnassigned />)} />
      <Route path="/dashboard/life-happened" element={dash(<LifeHappened />)} />
      <Route path="/dashboard/expected-income" element={dash(<ExpectedIncome />)} />
      <Route path="/dashboard/help" element={dash(<HelpCenter />)} />
      <Route path="/dashboard/admin/analytics" element={adminOnly(<AdminAnalytics />)} />
      <Route path="/dashboard/admin/support" element={adminOnly(<AdminSupport />)} />
      <Route path="/dashboard/admin/access" element={ownerOnly(<AdminAccess />)} />
      <Route path="/dashboard/foundation/:section" element={dash(<FoundationEdit />)} />
      <Route path="/dashboard/sprint" element={dash(<DashboardSprint />)} />
      <Route path="/dashboard/goal" element={dash(<DashboardGoal />)} />
      <Route path="/dashboard/progress" element={dash(<DashboardProgress />)} />
      <Route path="/dashboard/profile" element={dash(<DashboardProfile />)} />

      {/* Preserved product app (demo calc engine, entitlement-gated) */}
      <Route path="/app" element={app(<Home />)} />
      <Route path="/sprint" element={app(<MySprint />)} />
      <Route path="/foundation" element={app(<FoundationSetup />)} />
      <Route path="/goal" element={app(<MyGoal />)} />
      <Route path="/progress" element={app(<Progress />)} />
      <Route path="/profile" element={app(<Profile />)} />
    </Routes>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <StateProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </StateProvider>
  );
}

export default App;
